// Apple-style scroll animation engine for TFK Flower
// Wires up: split-word reveals, stagger lists, image zoom-in, parallax, hero fade,
// number counters, cursor glow, and a sticky statement section.

(function () {
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  // ── 1. Split text into per-word spans for the masked reveal ──
  // IMPORTANT: skip wrapping any element that already has its own layout role
  // (.line, .zh, .accent, .italic, .zh-line, .word). Only wrap raw text nodes.
  const SKIP_SPLIT = /\b(word|line|zh|zh-line|accent|italic|signature)\b/;
  const splitWords = (el) => {
    if (el.dataset.split === "1") return;
    el.dataset.split = "1";
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const parts = node.nodeValue.split(/(\s+)/);
        const frag = document.createDocumentFragment();
        parts.forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
          } else {
            const w = document.createElement("span");
            w.className = "word";
            const inner = document.createElement("span");
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          }
        });
        node.parentNode.replaceChild(frag, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Recurse INTO structural spans (.line/.zh/.italic/.accent) but don't wrap them.
        Array.from(node.childNodes).forEach(walk);
      }
    };
    Array.from(el.childNodes).forEach(walk);
    el.classList.add("split-words");

    // Per-word stagger delay
    const inners = el.querySelectorAll(".word > span");
    inners.forEach((s, i) => {
      s.style.transitionDelay = Math.min(i * 35, 800) + "ms";
    });
  };

  // ── 2. IntersectionObserver to trigger reveals ──
  const setupReveals = () => {
    const targets = [
      ".hero-title",
      ".section-header h2",
      ".atelier-text h3",
      ".customizer-text h3",
      ".wedding-content h2",
      ".newsletter h3",
    ];
    targets.forEach((sel) => $$(sel).forEach(splitWords));

    // Statement-pin: do a simple whole-block fade-in instead of word-split,
    // since the heading uses <br> + <span class="italic"> which the splitter
    // can fragment in ways that lose context.
    $$(".pin-content").forEach((el) => el.classList.add("fade-up"));

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -40px 0px" }
    );

    [
      ".split-words",
      ".stagger-up",
      ".zoom-in",
      ".accent-ribbon",
      ".fade-up",
    ].forEach((sel) => $$(sel).forEach((el) => io.observe(el)));
  };

  // ── 3. Parallax + hero scroll driver ──
  const setupScrollDrivers = () => {
    const parallaxEls = $$(".parallax, .parallax-photo");
    const hero = document.querySelector(".hero");
    const statementPins = $$(".statement-pin");

    let ticking = false;

    const update = () => {
      const vh = window.innerHeight;

      // Parallax: -1 above, 0 centered, +1 below
      parallaxEls.forEach((el) => {
        const r = el.getBoundingClientRect();
        const center = r.top + r.height / 2;
        const p = (center - vh / 2) / vh; // -1..+1 over the viewport
        el.style.setProperty("--p", Math.max(-1.2, Math.min(1.2, p)).toFixed(3));
      });

      // Hero scroll progress (0 at top, 1 by hero bottom)
      if (hero) {
        const r = hero.getBoundingClientRect();
        const total = r.height;
        const scrolled = Math.max(0, -r.top);
        const hp = Math.min(1, scrolled / total);
        hero.style.setProperty("--hp", hp.toFixed(3));
      }

      // Statement-pin progress (0 at first contact, 1 when leaving)
      statementPins.forEach((el) => {
        const r = el.getBoundingClientRect();
        const total = r.height - vh;
        const scrolled = Math.max(0, -r.top);
        const p = total > 0 ? Math.min(1, scrolled / total) : 0;
        el.style.setProperty("--p", p.toFixed(3));
      });

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
  };

  // ── 4. Number counters on the atelier stats ──
  const setupCounters = () => {
    const els = $$(".counter");
    const animate = (el) => {
      if (el.dataset.running === "1" || el.dataset.done === "1") return;
      el.dataset.running = "1";
      const target = parseFloat(el.dataset.to || "0");
      const dur = parseInt(el.dataset.dur || "1600", 10);
      const start = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const v = Math.round(target * ease(t));
        el.textContent = v.toLocaleString();
        if (t < 1) requestAnimationFrame(tick);
        else el.dataset.done = "1";
      };
      requestAnimationFrame(tick);
    };

    // Use a stricter trigger — must be at least 60% into viewport
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            animate(e.target);
            io.unobserve(e.target);
          }
        });
      },
      {
        // Only trigger when element is well within the viewport,
        // not when first peeking at the bottom edge.
        threshold: [0.5, 0.75, 1.0],
        rootMargin: "-15% 0px -15% 0px",
      }
    );

    els.forEach((el) => {
      el.textContent = "0";
      el.dataset.running = "0";
      el.dataset.done = "0";
      // Always wait for the user to scroll to it. Don't fire on boot —
      // even if it happens to be in view, we want the user to actually
      // see the count-up animation play.
      io.observe(el);
    });
  };

  // ── 5. Cursor-follow glow on hero ──
  const setupCursorGlow = () => {
    const hero = document.querySelector(".hero");
    if (!hero) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const glow = document.createElement("div");
    glow.className = "cursor-glow";
    hero.appendChild(glow);
    hero.addEventListener("mouseenter", () => hero.classList.add("has-cursor"));
    hero.addEventListener("mouseleave", () => hero.classList.remove("has-cursor"));
    hero.addEventListener("mousemove", (e) => {
      const r = hero.getBoundingClientRect();
      glow.style.left = e.clientX - r.left + "px";
      glow.style.top = e.clientY - r.top + "px";
    });
  };

  // ── 6. Magnetic buttons ──
  const setupMagneticButtons = () => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    $$(".btn, .btn-soft, .btn-solid").forEach((btn) => {
      btn.addEventListener("mousemove", (e) => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.18}px)`;
      });
      btn.addEventListener("mouseleave", () => {
        btn.style.transform = "";
      });
    });
  };

  // ── Reveal failsafe: if anything is still hidden after 2.5s, force it visible.
  // (Covers cases where IntersectionObserver misfires or the section is taller
  // than viewport so the threshold never trips.)
  const setupRevealFailsafe = () => {
    setTimeout(() => {
      $$(".split-words, .stagger-up, .zoom-in, .accent-ribbon, .fade-up").forEach((el) => {
        if (!el.classList.contains("in")) el.classList.add("in");
      });
    }, 2500);
  };

  // ── Boot when DOM is ready (after React mounts) ──
  const boot = () => {
    setupReveals();
    setupScrollDrivers();
    setupCounters();
    setupCursorGlow();
    setupMagneticButtons();
    setupRevealFailsafe();
  };

  // Wait for React to mount, then init.
  // Re-run once content is settled.
  const tryBoot = (attempt = 0) => {
    if (document.querySelector(".hero")) {
      boot();
    } else if (attempt < 30) {
      setTimeout(() => tryBoot(attempt + 1), 100);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => tryBoot());
  } else {
    tryBoot();
  }
})();
